import React from 'react';
import type { NumberRatingQuestion as NumberRatingQuestionType } from './survey.types';
import { useLanguageStore } from '../../stores/languageStore';

interface NumberRatingQuestionProps {
  question: NumberRatingQuestionType;
  value: number | null;
  onChange: (value: number) => void;
  error?: string;
  displayNumber?: number;
}

export const NumberRatingQuestion: React.FC<NumberRatingQuestionProps> = ({
  question,
  value,
  onChange,
  error,
  displayNumber,
}) => {
  const languageCode = useLanguageStore((state) => state.code);
  const { min, max, min_label, max_label } = question.scale;
  const numbers = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const stripLeadingValue = (label: string) =>
    label.replace(/^\s*\d+\s*[-–—:.()\]]*\s*/u, '').trim();
  const minLabelText = stripLeadingValue(min_label[languageCode] || min_label['en'] || '');
  const maxLabelText = stripLeadingValue(max_label[languageCode] || max_label['en'] || '');

  const handleNumberClick = (num: number) => {
    onChange(num);
  };

  return (
    <div className="survey-question number-rating-question">
      <label className="question-label">
        {displayNumber != null && (
          <span className="question-number">{displayNumber}.</span>
        )}
        <span dangerouslySetInnerHTML={{ __html: question.question_text[languageCode] }} />
        {question.is_required && <span className="required-indicator">*</span>}
      </label>
      <div className="number-rating-container">
        {/* Mobile-only label row */}
        <div className="number-rating-labels-mobile">
          ({min} - {minLabelText}, {max} - {maxLabelText})
        </div>
        <div className="number-rating-track">
          <div className="number-buttons number-buttons-with-labels">
            <div className="number-label-cell number-label-min">
              {minLabelText}
            </div>
            {numbers.map((num) => (
              <div key={num} className="number-button-wrapper">
                <button
                  type="button"
                  className={`number-button ${value === num ? 'number-button-active' : ''}`}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handleNumberClick(num);
                  }}
                  onClick={() => handleNumberClick(num)}
                  aria-label={`Rate ${num}`}
                >
                  <span className="number-button-value">{num}</span>
                </button>
              </div>
            ))}
            <div className="number-label-cell number-label-max">
              {maxLabelText}
            </div>
          </div>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};
